import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShieldAlert, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext.jsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu.jsx';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  const navItems = [
    { name: 'Talent Network', path: '/talent-pool' },
    { name: 'Partners', path: '/partners' },
  ];

  const isActive = (path) => location.pathname === path;
  const jobsActive = location.pathname.startsWith('/jobs');
  const blogActive = location.pathname.startsWith('/insights');
  const careerHubActive = ['/early-careers', '/career-finder'].includes(location.pathname);
  const companyActive = ['/about', '/contact'].includes(location.pathname);
  const careerHubItems = [
    { name: 'Early Careers', path: '/early-careers' },
    { name: 'Career Finder', path: '/career-finder' },
  ];
  const companyItems = [
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/assets/luxe-jobs-london-logo.png"
              alt="LJL Logo"
              className="h-9 md:h-12 w-auto group-hover:opacity-90 transition-opacity"
            />
            <h1 className="text-brand-logo text-foreground">
              Luxe Jobs <span className="text-brand-accent">London</span>
            </h1>
          </Link>

          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <Link to="/jobs" className={`text-sm font-medium transition-colors duration-200 ${jobsActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>Jobs</Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 ${careerHubActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                  Career Hub <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 p-2">
                {careerHubItems.map(item => <DropdownMenuItem key={item.path} asChild className="cursor-pointer px-3 py-2.5"><Link to={item.path}>{item.name}</Link></DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/insights" className={`text-sm font-medium transition-colors duration-200 ${blogActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>Blog</Link>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative text-sm font-medium transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 ${companyActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                  Company <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44 p-2">
                {companyItems.map(item => <DropdownMenuItem key={item.path} asChild className="cursor-pointer px-3 py-2.5"><Link to={item.path}>{item.name}</Link></DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated && (
              <>
                <Link 
                  to="/admin/imports" 
                  className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors border border-border/50 bg-muted/30 px-2 py-1.5 rounded-md"
                  title="Admin Dashboard"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Admin
                </Link>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </>
            )}
            
            <Link to="/job-alerts">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 active:scale-[0.98] shadow-sm font-medium">
                Job Alerts
              </Button>
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background absolute w-full shadow-xl">
          <nav className="px-4 py-4 space-y-3">
            <Link
              to="/jobs"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium transition-colors duration-200 ${jobsActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Jobs
            </Link>
            <div>
              <p className={`py-2 text-base font-medium ${careerHubActive ? 'text-primary' : 'text-muted-foreground'}`}>Career Hub</p>
              <div className="space-y-1 border-l border-border pl-4">
                {careerHubItems.map(item => <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`block py-1.5 text-sm font-medium transition-colors ${isActive(item.path) ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>{item.name}</Link>)}
              </div>
            </div>
            <Link to="/insights" onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-base font-medium transition-colors ${blogActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>Blog</Link>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-base font-medium transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div>
              <p className={`py-2 text-base font-medium ${companyActive ? 'text-primary' : 'text-muted-foreground'}`}>Company</p>
              <div className="space-y-1 border-l border-border pl-4">
                {companyItems.map(item => <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`block py-1.5 text-sm font-medium transition-colors ${isActive(item.path) ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>{item.name}</Link>)}
              </div>
            </div>
            
            <div className="h-px bg-border my-2" />
            
            {isAuthenticated && (
              <>
                <Link
                  to="/admin/imports"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <ShieldAlert className="h-4 w-4" />
                  Admin Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors text-left"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </>
            )}

            <div className="pt-4 pb-2">
              <Link to="/job-alerts" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 active:scale-[0.98]">
                  Job Alerts
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
